import { z } from "zod";

// ---------------------------------------------------------------------------
// Store report imports (GRA-82 / GRA-96 / GRA-84)
//
// Storage layout:
//   Businesses/{businessId}/reportImports/{importId}
//
// One import is one upload batch: one or more report files (PDF/xlsx) that
// are parsed into daily store and department measures. Imports are written
// only through the `kpiImport` router, which is gated on the report-upload
// capability rather than on people-data visibility.
// ---------------------------------------------------------------------------

export const REPORT_IMPORT_STATUSES = [
  "uploading",
  "parsing",
  "ready",
  "failed",
  "committed",
] as const;
export type ReportImportStatus = (typeof REPORT_IMPORT_STATUSES)[number];

export interface ReportImportFile {
  fileName: string;
  storagePath: string;
  contentType: string;
}

export interface ReportImportRecord {
  id: string;
  businessId: string;
  status: ReportImportStatus;
  files: ReportImportFile[];
  /** uid of the member who uploaded. Every import records who made it. */
  importedBy: string;
  createdAt: string | null;
  updatedAt: string | null;
  committedAt: string | null;
  /** Human-readable reason when `status === "failed"`. */
  error: string | null;
  /** Set once parsed (status "ready" or "failed" with issues). */
  preview: ReportImportPreview | null;
  /** Set on commit. */
  storeTeamId: string | null;
  /** Values written on commit. */
  writtenCount: number | null;
}

export const ListReportImportsInput = z.object({
  businessId: z.string().min(1),
  limit: z.number().int().min(1).max(100).default(30),
});
export type ListReportImportsInput = z.infer<typeof ListReportImportsInput>;

// ---------------------------------------------------------------------------
// Upload, preview and commit (GRA-84)
// ---------------------------------------------------------------------------

/** Keep in sync with the reportImports block in apps/engine/storage.rules. */
export const REPORT_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
export const REPORT_UPLOAD_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
] as const;
export const REPORT_UPLOAD_MAX_FILES = 14;

const ISO_DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const CreateReportUploadInput = z.object({
  businessId: z.string().min(1),
  files: z
    .array(
      z.object({
        fileName: z.string().min(1).max(200),
        contentType: z.enum(REPORT_UPLOAD_MIME_TYPES),
        size: z.number().int().positive().max(REPORT_UPLOAD_MAX_BYTES),
      }),
    )
    .min(1)
    .max(REPORT_UPLOAD_MAX_FILES),
});
export type CreateReportUploadInput = z.infer<typeof CreateReportUploadInput>;

export const GetReportImportInput = z.object({
  businessId: z.string().min(1),
  importId: z.string().min(1),
});
export type GetReportImportInput = z.infer<typeof GetReportImportInput>;

export const ParseReportImportInput = z.object({
  businessId: z.string().min(1),
  importId: z.string().min(1),
  /** Day for files whose report carries no date (xlsx exports). */
  dateOverride: ISO_DAY.optional(),
});
export type ParseReportImportInput = z.infer<typeof ParseReportImportInput>;

/** Column label (normalised) → measure, confirmed by the store once per layout. */
export const ConfirmReportMappingInput = z.object({
  businessId: z.string().min(1),
  importId: z.string().min(1),
  fingerprint: z.string().min(1).max(64),
  mapping: z.record(z.string().min(1).max(200), z.string().min(1).max(64)),
});
export type ConfirmReportMappingInput = z.infer<typeof ConfirmReportMappingInput>;

/**
 * What to do with one report department. "team" maps it to an existing team:
 * one already under the store, or a root team without sub-teams, which the
 * commit moves under the store (owner/admin only). Several departments may
 * share one team; their numbers are added together.
 */
export const ReportDepartmentAction = z.union([
  z.object({ kind: z.literal("create") }),
  z.object({ kind: z.literal("ignore") }),
  z.object({ kind: z.literal("team"), teamId: z.string().min(1) }),
]);
export type ReportDepartmentAction = z.infer<typeof ReportDepartmentAction>;

export const CommitReportImportInput = z.object({
  businessId: z.string().min(1),
  importId: z.string().min(1),
  /** The store the report belongs to: an existing root team or a new one. */
  store: z.union([
    z.object({ teamId: z.string().min(1) }),
    z.object({
      create: z.object({
        name: z.string().min(1).max(120),
        chain: z.enum(["ica", "coop", "other"]).optional(),
      }),
    }),
  ]),
  /**
   * Department code → what to do with it. Codes left out use the store's
   * saved mapping; a code with none is kept in the import only.
   */
  departments: z.record(z.string().min(1).max(40), ReportDepartmentAction).default({}),
});
export type CommitReportImportInput = z.infer<typeof CommitReportImportInput>;

export const ListStoreMeasuresInput = z.object({
  businessId: z.string().min(1),
  from: ISO_DAY,
  /** Exclusive. */
  to: ISO_DAY,
  storeTeamId: z.string().min(1).optional(),
});
export type ListStoreMeasuresInput = z.infer<typeof ListStoreMeasuresInput>;

/** One file in an import preview. */
export interface ReportImportFilePreview {
  fileName: string;
  source: "xlsx" | "pdf";
  date: string | null;
  dateTo: string | null;
  compareDate: string | null;
  store: string | null;
  filter: string | null;
  departmentCount: number;
  measures: string[];
  /** Layout id; a confirmed mapping is saved under it. */
  fingerprint: string;
  /** Unknown column labels with the model's suggestion ("ignore" if none). */
  unmapped: { column: string; suggestion: string }[];
}

export interface ReportImportIssue {
  severity: "error" | "warning";
  code: string;
  message: string;
  fileName?: string;
  department?: string;
}

export interface ReportImportDepartmentPreview {
  code: string;
  name: string;
  /** The team it maps to under the chosen store, if mapped before. */
  teamId: string | null;
  ignored: boolean;
  /** Never seen for this store: the UI asks what to do with it. */
  isNew: boolean;
}

export interface ReportImportTeamOption {
  teamId: string;
  name: string;
  parentTeamId: string | null;
  hasChildren: boolean;
}

export interface ReportImportPreview {
  files: ReportImportFilePreview[];
  /** Days the import covers (one per distinct report date). */
  dates: string[];
  /** Store name as printed in the report, if any. */
  storeName: string | null;
  /** Store teams (root teams) the import could belong to. */
  storeCandidates: { teamId: string; name: string }[];
  /**
   * Every team in the business, so a department can go to a team that
   * already exists: one under the chosen store, or a root team without
   * sub-teams that the commit moves under the store.
   */
  teams: ReportImportTeamOption[];
  departments: ReportImportDepartmentPreview[];
  /** Store totals per day for the preview line (kronor and counts). */
  totals: { date: string; measure: string; actual: number; lastYear: number | null }[];
  issues: ReportImportIssue[];
  /** No errors and nothing left to map or date. */
  canCommit: boolean;
}
